import {
  getActualWater,
  getWaterPercentValue,
} from './water-utils';
import { toNumber } from '../../../utils/numbers';
import logger from '../../../utils/logger';
import convertUnit from '../../../utils/unit-converter';
import { MeteredMaterialType } from '../../../constants/MaterialModels';
import { AdjustedMixDesignQtyType } from '../../../constants/AdjustedMixDesignQtyType';
import { isFeatureEnabled } from '../../../utils/feature-flags';
import {
  isMeteredWater,
  isWeighedWater,
} from './material-utils';
import { calculateIceAdjustment } from './calculate-ice-adjustment';
import { IceUnits } from '../../../constants/IceUnit';

const FEATURE_AUTOMOISTURE_FOR_METERED = isFeatureEnabled('automoisture_for_metered');
const FEATURE_LIVE_MOISTURE_FOR_WEIGHED = isFeatureEnabled('live_moisture_for_weighed');

const getCementQtyFromMixDesign = (values, materialStore) => {
  return values.mixDesign.filter(v => materialStore.isCement(v.constituentItemId) || materialStore.isCementitious(v.constituentItemId)).map(v => v.quantityNet).reduce((p, c) => p + c, 0);
};

const getCementQtyFromManuallyAddedConstituents = (values, materialStore) => {
  const items = Array.isArray(values?.itemsToBeManuallyAdded) ? values.itemsToBeManuallyAdded : [];

  return items
    .filter(v => materialStore.isCement(v.constituentItemId) || materialStore.isCementitious(v.constituentItemId))
    .map(v => toNumber(v.quantity, 2) || 0)
    .reduce((p, c) => p + c, 0);
};

const calculateByCement = (admixQty = 0, cementQty = 0) => {
  return (cementQty/100) * admixQty;
};

/**
 * Calculates the water quantity adjustment when using second water percentage split.
 *
 * When secondWater is enabled, the total net water quantity is split between regular water
 * and second water constituents based on the secondWater percentage.
 */
const calculateSecondWaterAdjustment = (
  waterQuantityNet,
  secondWater,
  isWater,
  isSecondWater,
  watersLength,
  secondWatersLength
) => {
  if (isWater) {
    // Regular water gets its share minus the percentage allocated to second water
    return (waterQuantityNet / watersLength) - (waterQuantityNet / watersLength * (secondWater / 100));
  } else if (isSecondWater) {
    // Second water gets the percentage allocated to it
    return (waterQuantityNet / secondWatersLength) * (secondWater / 100);
  }
  return 0;
};

export const calculateMixDesign = (values, rootStore) => {
  const { materialStore, settingsStore, ticketStore, slumpTableStore } = rootStore;
  if (!values?.mixDesign?.length) {
    return values;
  }

  const { configuration } = settingsStore;
  let waterInTruck = Number(values.waterInTruck) / values.quantityToBeBatched || 0;
  const secondWater = values.secondWater ? Number(values.secondWater) : 0;
  const waters = values.mixDesign.filter(v => materialStore.isWater(v.constituentItemId) && (v.quantity > 0 || v.trim > 0 || (v.added && secondWater)));
  const secondWaters = values.mixDesign.filter(v => materialStore.isSecondWater(v.constituentItemId));
  const secondWatersWithValue = secondWaters.filter(v => v.quantity > 0 || v.trim > 0);
  const waterSize = secondWatersWithValue.length + waters.length || 0;
  let waterAdjust = configuration.waterAdjustByLoad
    ? Number(values.waterAdjust || 0) / Number(values.batchSize)
    : Number(values.waterAdjust || 0);
  const globalWaterUnitSymbol = ticketStore.getWaterUnit();
  const waterAdjustmentFromSlumpTable = getWaterAdjustmentFromSlumpTable(values, slumpTableStore, settingsStore);

  if (values.waterUnit !== globalWaterUnitSymbol) {
    waterAdjust = toNumber(convertUnit(waterAdjust, globalWaterUnitSymbol, values.waterUnit), 2);
    waterInTruck = toNumber(convertUnit(waterInTruck, globalWaterUnitSymbol, values.waterUnit), 2);
  }

  if (!values.mixDesign.length) {
    return values;
  }

  const waterUnitSymbol = waters.length ? waters[0].unitSymbol : '';
  let autoMoistureDeduction = 0;
  let waterQuantity = 0;
  let waterPercent = 0;

  values.mixDesign.forEach((v, i) => {
    if (v.moisture) {
      const moistureDeduction = getActualWater(v, values, waterUnitSymbol, materialStore);
      autoMoistureDeduction += moistureDeduction;
      values.mixDesign[i].moistureDeduction = moistureDeduction;
    }
    if (materialStore.isWater(v.constituentItemId) || materialStore.isSecondWater(v.constituentItemId)) {
      // Include trim in waterQuantity when secondWater is used, so the split accounts for it
      waterQuantity += v.quantity + (secondWater ? (toNumber(v.trim, 2) || 0) : 0);
    }
    if (materialStore.isAdmixture(v.constituentItemId) || (v.batchCode && v.materialType === MeteredMaterialType.Admixture)) {
      waterPercent += getWaterPercentValue(v, waterUnitSymbol, materialStore);
    }
  });

  let iceAdjustment = calculateIceAdjustment(values.ice, values.iceUnit, values.quantityToBeBatched, waterUnitSymbol);

  let waterQuantityNet = waterQuantity - autoMoistureDeduction - waterPercent - waterInTruck - iceAdjustment + waterAdjust + waterAdjustmentFromSlumpTable;

  logger.debug('waters', waters.length, 'secondWaters', secondWaters.length, 'secondWater %', secondWater,
    'waterQuantity', waterQuantity, 'waterQuantityNet',  waterQuantityNet, 'autoMoistureDeduction', autoMoistureDeduction,
    'waterInTruck', waterInTruck, 'iceAdjustment', iceAdjustment, 'waterAdjust', waterAdjust,
    'waterPercent', waterPercent, 'waterAdjustmentFromSlumpTable', waterAdjustmentFromSlumpTable);

  values.mixDesign.forEach((constituent, i) => {
    const trim = toNumber(constituent.trim, 2) || 0;
    let adjustedQuantity = constituent.quantity;

    if (materialStore.isWater(constituent.constituentItemId) || materialStore.isSecondWater(constituent.constituentItemId)) {
      if (secondWater) {
        // When secondWater is used, trim is already in waterQuantityNet, don't add it again
        adjustedQuantity = calculateSecondWaterAdjustment(
          waterQuantityNet,
          secondWater,
          materialStore.isWater(constituent.constituentItemId),
          materialStore.isSecondWater(constituent.constituentItemId),
          waters.length,
          secondWaters.length
        );
      } else if (!adjustedQuantity && !trim) {
        adjustedQuantity = constituent.quantity;
      } else {
        // When secondWater is not used, calculate adjustments and add trim at the end
        adjustedQuantity = constituent.quantity - (autoMoistureDeduction / waterSize) - (waterPercent / waterSize) - (waterInTruck / waterSize) - (iceAdjustment / waterSize) + (waterAdjust / waterSize) + (waterAdjustmentFromSlumpTable / waterSize);
      }
    }

    if (materialStore.isWater(constituent.constituentItemId)) {
      // Add trim to water only when secondWater is not being used
      values.mixDesign[i].quantityNet = toNumber(adjustedQuantity + (secondWater ? 0 : trim), 2);
    } else if (materialStore.isSecondWater(constituent.constituentItemId)) {
      // For second water, trim is already included in adjusted quantity
      values.mixDesign[i].quantityNet = toNumber(adjustedQuantity, 2);
    } else {
      values.mixDesign[i].quantityNet = toNumber(adjustedQuantity + trim, 2);
    }
    values.autoMoistureDeduction = autoMoistureDeduction;
    logger.debug('calculateMixDesign', constituent.constituentItemCode, constituent.quantity, constituent.quantityNet, autoMoistureDeduction, constituent);
  });

  const cementQty = getCementQtyFromMixDesign(values, materialStore) + getCementQtyFromManuallyAddedConstituents(values, materialStore);

  values.mixDesign.forEach((v, i) => {
    if (v.byCement && cementQty) {
      values.mixDesign[i].quantityNet = calculateByCement(v.quantityNet, cementQty);
    }
  });

  return values;
};

export const getQuantityTypeUnitForAdjustedMix = (item, settingsStore) => {
  const { configuration } = settingsStore;
  const measurementSystem = configuration?.measurement;

  if (
    measurementSystem
    && [AdjustedMixDesignQtyType.Ice, AdjustedMixDesignQtyType.IcePerVolume]
      .includes(item.quantityType)
  ) {
    const units = IceUnits[measurementSystem] || [];
    return units.find(v => v.isCompoundUnit === (AdjustedMixDesignQtyType.IcePerVolume === item.quantityType))?.label || units[0]?.label || item.unitSymbol;
  }

  return [AdjustedMixDesignQtyType.Percentage, AdjustedMixDesignQtyType.SecondWater].includes(item.quantityType)
    ? '%'
    : item.unitSymbol;
};

export const adjustMixDesign = (adjustedMixDesignId, values, mixId, rootStore) => {
  const { mixStore, materialStore, batchStore, devicesStore, settingsStore } = rootStore;
  const { adjustedMixDesigns } = mixStore;
  const { mixDesign = [], extra = [] } = values;
  let adjustedMixes = [];
  const adjustedMix = adjustedMixDesignId
    ? adjustedMixDesigns.find(v => v.id === Number(adjustedMixDesignId))
    : adjustedMixDesigns.filter(v => extra.find(x => x.itemId === v.extraProductItemId));

  if (Array.isArray(adjustedMix)) {
    adjustedMixes = [].concat(adjustedMix);
  } else if (adjustedMix) {
    adjustedMixes.push(adjustedMix);
  }

  let md = [].concat(mixDesign.map(v => Object.assign({}, v)));

  if (adjustedMixes.length) {
    adjustedMixes.forEach(mix => {
      mix.constituentItems.forEach(v => {
        if (!md.find(c => c.constituentItemId === v.constituentItemId)) {
          md.push({
            ...v,
            quantity: 0,
            trim: 0,
            added: true,
          });
        }
      });
    });
  } else {
    md = checkMixDesignQuantityAndTrim(Number(mixId), values, mixStore, materialStore, batchStore, devicesStore).mixDesign;
    md = [].concat(md.map(v => Object.assign({}, v)));
  }

  md.forEach((v, i) => {
    adjustedMixes.forEach(mix => {
      const constituent = adjustedMixes.length ? mix.constituentItems.find(c => c.constituentItemId === v.constituentItemId) : null;
      if (constituent) {
        if (constituent.quantityType === AdjustedMixDesignQtyType.Percentage) {
          const prevQty = md[i].trim ? (md[i].quantity + md[i].trim || 0) : md[i].quantity;
          md[i].trim = (md[i].trim || 0) + (prevQty * ((constituent.quantity || 0) / 100));
        } else if (constituent.quantityType === AdjustedMixDesignQtyType.Cumulative) {
          md[i].trim = (md[i].trim || 0) + constituent.quantity || 0;
        } else if (
          constituent.quantityType === AdjustedMixDesignQtyType.SecondWater
          && materialStore.isSecondWater(constituent.constituentItemId)
        ) {
          values.secondWater = constituent.quantity;
        } else if (
          [AdjustedMixDesignQtyType.Ice, AdjustedMixDesignQtyType.IcePerVolume]
            .includes(constituent.quantityType)
        ) {
          values.ice = constituent.quantity;
          const measurementSystem = settingsStore?.configuration?.measurement;
          const units = measurementSystem ? (IceUnits[measurementSystem] || []) : [];
          const unit = units.find(u => u.isCompoundUnit === (AdjustedMixDesignQtyType.IcePerVolume === constituent.quantityType)) || units[0];
          values.iceUnit = unit?.key || values.iceUnit;
        } else {
          md[i].trim = (0 - md[i].quantity) + constituent.quantity || 0;
        }

        if (materialStore.isAdmixture(v.constituentItemId)) {
          md[i].byCement = constituent.byCement;
        }

        logger.log('adjust mix design: ', constituent.constituentItemCode, constituent.quantity, constituent.quantityType, constituent.byCement);
      } else if (materialStore.isAdmixture(v.constituentItemId)) {
        const material = materialStore.materialsWithDetails.find(m => m.itemId === v.constituentItemId) || {};
        if (material.byCement) {
          md[i].byCement = material.byCement;
        }
      }
      md[i].moisture = getMoisture(v.constituentItemId, materialStore);
    });
  });

  md = checkMixDesignOrder(md, materialStore);

  return {
    ...values,
    adjustedMixDesignId,
    adjustedMixes,
    mixDesign: md,
    extra,
  };
};

export const checkMixDesignQuantityAndTrim = (mixId, values = {}, mixStore, materialStore, batchStore, devicesStore) => {
  const mixDesignTrim = mixId ? mixStore.getMixDesignTrim(mixId, values.dispatchOrderId) : [];
  let mixDesign =  mixId && !batchStore.orderId ? mixStore.getMixDesign(mixId) : values.mixDesign || [];
  mixDesign = [].concat(mixDesign.map(v => Object.assign({}, v)));

  mixDesign.forEach((v, i) => {
    const constituent = mixDesignTrim.find(c => v.constituentItemId === c.constituentItemId);
    if (constituent) {
      mixDesign[i].trim = constituent.trim;
      if (constituent.initialQuantity && constituent.quantity && constituent.initialQuantity !== constituent.quantity) {
        mixDesign[i].quantity = constituent.quantity;
        mixDesign[i].initialQuantity = constituent.initialQuantity;
      }
    }

    if (materialStore.isAdmixture(v.constituentItemId)) {
      const material = materialStore.materialsWithDetails.find(m => m.itemId === v.constituentItemId) || {};
      if (material.byCement) {
        mixDesign[i].byCement = material.byCement;
      }
    }

    mixDesign[i].moisture = getMoisture(v.constituentItemId, materialStore);

    if (devicesStore.getActiveBinsForMultiScale(v)) {
      mixDesign[i].binPercent = getBinPercent(v, materialStore);
    }
  });

  return {
    ...values,
    mixDesign,
  };
};

export const getMoisture = (materialId, materialStore) => {
  const material = materialStore.materials.find(v => v.itemId === Number(materialId));
  const md = material ? materialStore.materialDetails[material.materialDetailId] || {} : {};
  let moisture = md.moistureAmount || 0;

  if (md.liveMoisture && (FEATURE_AUTOMOISTURE_FOR_METERED || FEATURE_LIVE_MOISTURE_FOR_WEIGHED)) {
    const initialMoisture = (md.autoMoistureAmount - (md.changeLimitForMoisture || 1));
    moisture = initialMoisture > md.autoMoistureMinAmount ? initialMoisture : md.autoMoistureMinAmount;
  } else if (md.autoMoisture) {
    moisture = md.autoMoistureAmount || 0;
  }

  return toNumber(moisture, 2);
};

export const checkMixDesignOrder = (mixDesign = [], materialStore) => {
  return mixDesign.sort((a, b) => {
    const m1 = materialStore.materialsWithDetails.find(v => v.itemId === a.constituentItemId) || {};
    const m2 = materialStore.materialsWithDetails.find(v => v.itemId === b.constituentItemId) || {};
    return (m1.batchOrder || a.order || 100) - (m2.batchOrder || b.order || 100);
  });
};

export const getBinPercent = (constituent, materialStore) => {
  const m = constituent.batchCode
    ? materialStore.getMaterialByBatchCode(constituent.batchCode)
    : materialStore.getMaterialByItemId(Number(constituent.constituentItemId));
  const md = m ? materialStore.materialDetails[m.materialDetailId] : null;
  const binPercent = md && md.bins ? md.bins.filter(v => v.isActive).map(v => { return { [v.binId]: v.split };}) : {};

  return Object.assign({}, ...binPercent);
};

export const isMeteredWaterInTheMix = (values, materialStore) => {
  return !!values?.mixDesign.find(v => v.quantityNet > 0 && isMeteredWater(v.constituentItemId, materialStore));
};

export const isWeighedWaterInTheMix = (values, materialStore) => {
  return !!values?.mixDesign.find(v => v.quantityNet > 0 && isWeighedWater(v.constituentItemId, materialStore));
};

const getWaterAdjustmentFromSlumpTable = (values, slumpTableStore, settingsStore) => {
  const { configuration } = settingsStore;
  const slumpDiff = Number(values.slump) - Number(values.originalSlump);
  const slumpTable = slumpTableStore?.getSlumpTableById(values.slumpTableId || values.mix?.slumpTableId || configuration?.slumpTableId);
  const waterAdjustment = slumpTableStore?.calculateCumulativeChange(slumpDiff, slumpTable?.steps);
  logger.log('slump table:', slumpTable?.code, 'slumpDiff:', slumpDiff,
    'slump change', values.originalSlump, '->', values.slump, 'water adjustment:', waterAdjustment);
  return waterAdjustment || 0;
};

/**
 * Validates and limits a negative trim value to prevent the constituent's quantityNet from going negative.
 *
 * This function handles complex scenarios like water with secondWater percentage, where trim affects
 * the total water quantity which then gets split.
 *
 * @param {number} proposedTrim - The trim value the user wants to apply (negative to reduce quantity)
 * @param {number} index - Index of the constituent in mixDesign array
 * @param {object} values - Current form values
 * @returns {number} The validated trim value (limited to not make quantityNet negative)
 */
export const validateNegativeTrim = (proposedTrim, index, values, rootStore) => {
  // Positive trims don't need validation
  if (proposedTrim >= 0) {
    return proposedTrim;
  }

  // Step 1: Check if the user's proposed trim would make the quantity negative
  let testPayload = {...values };
  testPayload.mixDesign[index] = {
    ...values.mixDesign[index],
    trim: proposedTrim,
  };
  const testResult = calculateMixDesign(testPayload, rootStore);
  const testQuantity = testResult.mixDesign[index].quantityNet || 0;

  if (testQuantity < 0) {
    // Step 2: Calculate the base quantity (without this constituent's trim)
    let zeroTrimPayload = {...values };
    zeroTrimPayload.mixDesign[index] = {
      ...values.mixDesign[index],
      trim: 0,
    };
    const zeroTrimResult = calculateMixDesign(zeroTrimPayload, rootStore);
    const zeroTrimQuantity = zeroTrimResult.mixDesign[index].quantityNet || 0;

    // Step 3: Calculate how much the trim affects the final quantity (the "effect factor")
    const effect = (testQuantity - zeroTrimQuantity) / proposedTrim;

    // Step 4: Find the exact trim value that would make quantity = 0
    if (effect !== 0) {
      return -toNumber(zeroTrimQuantity / effect, 2);
    } else {
      // Edge case: if effect is 0, trim has no impact, just use the base quantity
      return -toNumber(zeroTrimQuantity, 2);
    }
  }

  return toNumber(proposedTrim, 2);
};
